// ============================================================================
// TOURNAMENT PRESENTER
// ----------------------------------------------------------------------------
// One place that turns a tournament document into what the lobby card and
// the detail sheet show: money strings, the phase it is in, the countdown
// line, the tags, the level clock, the first prize, and which buttons make
// sense for this viewer right now. Both views read from here so they can
// never disagree.
// ============================================================================
import { Tournament, TOURNAMENT_TIERS, calculatePrizePool, isRegistrationOpen } from '../../types/tournamentTypes';

export type TournamentPhase = 'registering' | 'starting' | 'late_reg' | 'live' | 'completed' | 'cancelled';

export interface ViewerContext {
  currentUserId?: string | null;
  isRegistered: boolean;
  userBalance: number;
  myTableId?: string;
  isAdminUser?: boolean;
}

export interface ActionModel {
  /** The one pill button on the card and the sticky button on the sheet */
  primary: { kind: 'register' | 'late_reg' | 'join' | 'spectate' | 'rebuy' | 'registered' | 'closed'; label: string; disabled?: boolean; hint?: string };
  /** Extra buttons only the detail sheet shows */
  secondary: Array<{ kind: 'unregister' | 'spectate' | 'results' | 'force_start' | 'rebuy'; label: string; disabled?: boolean }>;
  isBusted: boolean;
  hasStarted: boolean;
  rebuyWindowOpen: boolean;
}

const toMs = (v: any): number | null => {
  if (!v) return null;
  if (typeof v === 'number') return v;
  if (v instanceof Date) return v.getTime();
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  const n = new Date(v).getTime();
  return Number.isFinite(n) ? n : null;
};

export function isPlayCoinsTournament(t: Tournament): boolean {
  const tier = t.tier ? TOURNAMENT_TIERS[t.tier as keyof typeof TOURNAMENT_TIERS] : null;
  return (t.currency || tier?.currency || 'pkcash') === 'playcoins';
}

/** Money for the card: PlayCoins as 1.5K style, PSS Coins as $ with cents. */
export function fmtMoney(t: Tournament, n: number, opts: { compact?: boolean } = {}): string {
  const pc = isPlayCoinsTournament(t);
  if (pc) {
    if (opts.compact && n >= 1000) {
      const k = n / 1000;
      return `${k >= 100 ? Math.round(k) : k.toFixed(k >= 10 ? 1 : 2).replace(/\.?0+$/, '')}K`;
    }
    return Math.round(n).toLocaleString();
  }
  if (opts.compact && n >= 1000) return `$${(n / 1000).toFixed(2).replace(/\.?0+$/, '')}K`;
  return `$${n.toFixed(2)}`;
}

export function currencyLabel(t: Tournament): string {
  return isPlayCoinsTournament(t) ? 'PlayCoins' : 'PSS Coins';
}

export function phaseOf(t: Tournament): TournamentPhase {
  const s = t.status as string;
  if (s === 'completed') return 'completed';
  if (s === 'cancelled') return 'cancelled';
  if (s === 'late_reg') return 'late_reg';
  if (['running', 'in_progress', 'active', 'final_table', 'heads_up'].includes(s) || (t as any).tournamentStarted === true) return 'live';
  if (s === 'starting') return 'starting';
  return 'registering';
}

export function hasStarted(t: Tournament): boolean {
  const p = phaseOf(t);
  return p === 'late_reg' || p === 'live' || p === 'completed';
}

export function lateRegEndMs(t: Tournament): number | null {
  return toMs((t as any).lateRegistrationEndTime);
}

/** Prize pool the way the card shows it: real pool, or the guarantee if larger. */
export function prizePoolOf(t: Tournament): { pool: number; guaranteed: boolean; entrants: number } {
  const pc = isPlayCoinsTournament(t);
  const entrants = Math.max(t.currentPlayers || 0, (t.registeredPlayers || []).length);
  const rake = pc ? 0 : (t.houseRakePercent ?? 10);
  const { prizePool } = calculatePrizePool(entrants * (t.buyInAmount || 0), rake);
  const gtd = t.guaranteedPrize || 0;
  const live = Math.max(prizePool, t.prizePool || 0);
  return { pool: Math.max(live, gtd), guaranteed: gtd > live, entrants };
}

export function payoutsOf(t: Tournament): Array<{ position: number; amount: number; percentage: number }> {
  const { pool } = prizePoolOf(t);
  return (t.payoutStructure || []).map(p => ({
    position: p.position,
    percentage: p.percentage,
    amount: Math.floor(pool * (p.percentage / 100) * 100) / 100,
  }));
}

export function firstPrizeOf(t: Tournament): number {
  return payoutsOf(t)[0]?.amount || 0;
}

/** Short tags for the header: format, speed, table size, entry rules. */
export function tagsOf(t: Tournament): string[] {
  const tags: string[] = [];
  const type = (t.type || 'freezeout') as string;
  tags.push(type === 'freezeout' ? 'Freezeout' : type === 'rebuy' ? 'Rebuy' : type === 'knockout' ? 'Knockout' : type === 'satellite' ? 'Satellite' : type === 'freeroll' ? 'Freeroll' : type);
  const structure = String((t as any).structure || 'regular').toLowerCase();
  if (structure === 'turbo') tags.push('Turbo');
  if (structure === 'hyper') tags.push('Hyper');
  if (structure === 'deep') tags.push('Deep Stack');
  const perTable = t.maxPlayersPerTable || 9;
  tags.push(perTable <= 2 ? 'Heads-Up' : perTable <= 6 ? `${perTable}-Max` : 'Full Ring');
  tags.push('NLH');
  if ((t as any).rebuyAllowed !== false && type !== 'freezeout') tags.push('Re-Entry');
  if (t.guaranteedPrize) tags.push('GTD');
  if (isPlayCoinsTournament(t)) tags.push('No Rake');
  if ((t as any).media === 'voice') tags.push('Voice');
  if ((t as any).media === 'video') tags.push('Video');
  if ((t as any).entry === 'code') tags.push('Code Entry');
  if ((t as any).entry === 'group') tags.push('Crew Only');
  return tags;
}

/** Current level and the clock to the next one, when the server has written it. */
export function levelClock(t: Tournament, now: number): {
  level: number; sb: number; bb: number; ante: number;
  next?: { sb: number; bb: number };
  msToNext: number | null; onBreak: boolean;
} {
  const level = Math.max(1, t.currentBlindLevel || 1);
  const levels = t.blindLevels || [];
  const cur = levels[level - 1] || levels[levels.length - 1] || { smallBlind: 0, bigBlind: 0, ante: 0 };
  const nxt = levels[level];
  const started = toMs((t as any).blindLevelStartedAt) ?? toMs((t as any).currentLevelStartedAt);
  const durationMs = ((cur as any).duration || t.blindLevelDuration || 10) * 60 * 1000;
  const onBreak = (t as any).onBreak === true || (t as any).paused === true;
  const msToNext = started ? Math.max(0, started + durationMs - now) : null;
  return { level, sb: cur.smallBlind, bb: cur.bigBlind, ante: cur.ante || 0, next: nxt ? { sb: nxt.smallBlind, bb: nxt.bigBlind } : undefined, msToNext, onBreak };
}

export function fmtClock(ms: number, opts: { seconds?: boolean } = {}): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (opts.seconds || m < 10) return `${m}m ${s.toString().padStart(2, '0')}s`;
  return `${m} min`;
}

/** The one-line status on the card: what is happening and when. */
export function statusLine(t: Tournament, now: number): { text: string; tone: 'neutral' | 'warm' | 'live' | 'done' } {
  const phase = phaseOf(t);
  if (phase === 'completed') return { text: 'Finished', tone: 'done' };
  if (phase === 'cancelled') return { text: 'Cancelled', tone: 'done' };
  if (phase === 'late_reg') {
    const end = lateRegEndMs(t);
    const lc = levelClock(t, now);
    if (end && end > now) return { text: `Late reg ends in ${fmtClock(end - now)}`, tone: 'warm' };
    return { text: `Late reg open · Level ${lc.level}`, tone: 'warm' };
  }
  if (phase === 'live') {
    const lc = levelClock(t, now);
    return { text: `Live · Level ${lc.level} · ${lc.sb.toLocaleString()}/${lc.bb.toLocaleString()}`, tone: 'live' };
  }
  const startMs = toMs(t.startTime) || now;
  if (startMs <= now) return { text: 'Starting now', tone: 'warm' };
  const diff = startMs - now;
  if (diff < 60 * 60 * 1000) return { text: `Starts in ${fmtClock(diff, { seconds: true })}`, tone: 'warm' };
  const d = new Date(startMs);
  const today = new Date(now);
  const sameDay = d.toDateString() === today.toDateString();
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dayLabel = sameDay ? 'Today' : isTomorrow ? 'Tomorrow' : d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });
  return { text: `${dayLabel} ${time} · in ${fmtClock(diff)}`, tone: 'neutral' };
}

/** Which buttons this viewer gets. Mirrors the server gates; the server still decides. */
export function actionsFor(t: Tournament, ctx: ViewerContext, now: number): ActionModel {
  const started = hasStarted(t);
  const phase = phaseOf(t);
  const uid = ctx.currentUserId || '';
  const eliminated: string[] = Array.isArray((t as any).eliminatedPlayers) ? (t as any).eliminatedPlayers : [];
  const active: string[] = Array.isArray(t.activePlayers) ? t.activePlayers : [];
  const isBusted = !!uid && ctx.isRegistered && (eliminated.includes(uid) || (started && active.length > 0 && !active.includes(uid)));

  const rebuyAllowed = (t as any).rebuyAllowed !== false;
  const currentLevel = t.currentBlindLevel || 1;
  const lateRegLevels = t.lateRegLevels || 4;
  const end = lateRegEndMs(t);
  const paid = (t.payoutStructure || []).length;
  const alive = active.length;
  const hardClosed = (t as any).handForHand === true || (t as any).finalTableFormed === true || (t as any).finalTableForming === true || (paid > 0 && alive > 0 && alive <= paid);
  const rebuyWindowOpen = rebuyAllowed && !hardClosed && currentLevel <= lateRegLevels && (end === null || end > now);
  const canAfford = ctx.userBalance >= (t.buyInAmount || 0);
  const buyIn = fmtMoney(t, t.buyInAmount || 0);
  const canRegister = isRegistrationOpen(t) && phase !== 'live';

  const secondary: ActionModel['secondary'] = [];
  let primary: ActionModel['primary'];

  if (ctx.isRegistered) {
    if (isBusted) {
      primary = rebuyWindowOpen
        ? { kind: 'rebuy', label: `Rebuy ${buyIn}`, disabled: !canAfford, hint: canAfford ? undefined : `You need ${buyIn} to rebuy` }
        : { kind: 'spectate', label: 'Spectate' };
      if (rebuyWindowOpen) secondary.push({ kind: 'spectate', label: 'Spectate' });
      secondary.push({ kind: 'results', label: 'Live results' });
    } else if (ctx.myTableId) {
      primary = { kind: 'join', label: 'Join my table' };
      secondary.push({ kind: 'results', label: 'Live results' });
    } else if (started) {
      primary = { kind: 'spectate', label: 'Spectate' };
      secondary.push({ kind: 'results', label: 'Live results' });
    } else {
      primary = { kind: 'registered', label: 'Registered' };
      secondary.push({ kind: 'unregister', label: `Unregister · refund ${buyIn}` });
      if (ctx.isAdminUser) secondary.push({ kind: 'force_start', label: 'Admin: force start now' });
    }
  } else if (phase === 'late_reg' && canRegister) {
    primary = { kind: 'late_reg', label: 'Late Reg', disabled: !canAfford, hint: canAfford ? undefined : `You need ${buyIn}` };
    secondary.push({ kind: 'spectate', label: 'Spectate' });
  } else if (phase === 'live') {
    primary = { kind: 'spectate', label: 'Spectate' };
    secondary.push({ kind: 'results', label: 'Live results' });
  } else if (!canRegister) {
    primary = { kind: 'closed', label: phase === 'completed' ? 'Finished' : phase === 'cancelled' ? 'Cancelled' : 'Closed', disabled: true };
  } else {
    primary = { kind: 'register', label: `Register ${buyIn}`, disabled: !canAfford, hint: canAfford ? undefined : `You need ${buyIn}, you have ${fmtMoney(t, Math.floor(ctx.userBalance * 100) / 100)}` };
  }

  return { primary, secondary, isBusted, hasStarted: started, rebuyWindowOpen };
}
